import type { CmtsColumnSpec } from "../../components/CmtsEnterpriseDataGrid.js";
import { complaintCategoryApi } from "../../services/complaintCategoryApi.js";
import { complaintSourceApi } from "../../services/complaintSourceApi.js";
import { complaintSubcategoryApi } from "../../services/complaintSubcategoryApi.js";
import { priorityApi } from "../../services/priorityApi.js";
import { referenceDataApi } from "../../services/referenceDataApi.js";
import { referenceTypeApi } from "../../services/referenceTypeApi.js";
import { severityApi } from "../../services/severityApi.js";
import type { CrudApi, EntityCreateRequest, EntityRecord, EntitySchemaField, EntityUpdateRequest } from "../../types/api.types.js";

export type CmtsMasterDataCrudEntityKey =
  | "severity"
  | "priority"
  | "complaint-category"
  | "complaint-subcategory"
  | "complaint-source"
  | "reference-data"
  | "reference-type";

export type CmtsMasterDataCrudApiBinding = CrudApi<EntityRecord, EntityCreateRequest, EntityUpdateRequest>;

export type CmtsMasterDataDomainRuleCode =
  | "DUPLICATE_BUSINESS_KEY"
  | "CROSS_TENANT_SCOPE"
  | "PARENT_REQUIRED"
  | "PARENT_MUST_BE_ACTIVE"
  | "ACTIVE_CHILD_DEPENDENCY_CHECK"
  | "DROPDOWN_CONSUMER_REFRESH"
  | "EFFECTIVE_DATE_ORDER";

export type CmtsMasterDataDomainRule = {
  readonly code: CmtsMasterDataDomainRuleCode;
  readonly message: string;
};

export type CmtsMasterDataParentConfig = {
  readonly entityKey: CmtsMasterDataCrudEntityKey;
  readonly fieldName: string;
  readonly required: boolean;
  readonly mustBeActive: boolean;
  readonly dropdownLabel: string;
  readonly inactiveMessage: string;
};

export type CmtsMasterDataCrudEntityConfig = {
  readonly entityKey: CmtsMasterDataCrudEntityKey;
  readonly masterType: string;
  readonly routeSegment: string;
  readonly routePath: string;
  readonly pageFile: string;
  readonly serviceFile: string;
  readonly serviceName: string;
  readonly title: string;
  readonly singularTitle: string;
  readonly description: string;
  readonly permission: {
    readonly view: string;
    readonly create: string;
    readonly edit: string;
    readonly activate: string;
    readonly deactivate: string;
    readonly delete: string;
  };
  readonly api: CmtsMasterDataCrudApiBinding;
  readonly columns: readonly CmtsColumnSpec<EntityRecord>[];
  readonly schemaMode: "backend" | "static-fallback";
  readonly defaultSort: {
    readonly sortBy: string;
    readonly sortDirection: "asc" | "desc";
  };
  readonly businessKey: readonly string[];
  readonly parent?: CmtsMasterDataParentConfig | undefined;
  readonly dropdownConsumers: readonly string[];
  readonly dependencyTargets: readonly string[];
  readonly domainRules: readonly CmtsMasterDataDomainRule[];
  readonly fallbackFields: readonly EntitySchemaField[];
};

const masterDataPermission = {
  view: "AUTH.USER_MANAGE",
  create: "AUTH.USER_MANAGE",
  edit: "AUTH.USER_MANAGE",
  activate: "AUTH.USER_MANAGE",
  deactivate: "AUTH.USER_MANAGE",
  delete: "AUTH.USER_MANAGE",
} as const;

const commonColumns: readonly CmtsColumnSpec<EntityRecord>[] = [
  { key: "code", header: "Code", sortable: true, filterable: true, width: 150 },
  { key: "label", header: "Label", sortable: true, filterable: true, width: 220 },
  { key: "description", header: "Description", sortable: true, filterable: true, width: 320 },
  { key: "status", header: "Status", sortable: true, filterable: true, width: 130 },
  { key: "sortOrder", header: "Sort", sortable: true, filterable: false, width: 90, align: "right" },
  { key: "updatedAt", header: "Updated", sortable: true, filterable: false, width: 170 },
];

const categoryColumns: readonly CmtsColumnSpec<EntityRecord>[] = [
  { key: "code", header: "Code", sortable: true, filterable: true, width: 150 },
  { key: "label", header: "Category", sortable: true, filterable: true, width: 220 },
  { key: "parentCategoryCode", header: "Parent", sortable: true, filterable: true, width: 170 },
  { key: "status", header: "Status", sortable: true, filterable: true, width: 130 },
  { key: "usageCount", header: "Usage", sortable: true, filterable: false, width: 100, align: "right" },
  { key: "updatedAt", header: "Updated", sortable: true, filterable: false, width: 170 },
];

const subcategoryColumns: readonly CmtsColumnSpec<EntityRecord>[] = [
  { key: "code", header: "Code", sortable: true, filterable: true, width: 150 },
  { key: "label", header: "Subcategory", sortable: true, filterable: true, width: 220 },
  { key: "parentCategoryLabel", header: "Category", sortable: true, filterable: true, width: 220 },
  { key: "status", header: "Status", sortable: true, filterable: true, width: 130 },
  { key: "sortOrder", header: "Sort", sortable: true, filterable: false, width: 90, align: "right" },
  { key: "updatedAt", header: "Updated", sortable: true, filterable: false, width: 170 },
];

const referenceDataColumns: readonly CmtsColumnSpec<EntityRecord>[] = [
  { key: "code", header: "Code", sortable: true, filterable: true, width: 150 },
  { key: "label", header: "Reference Value", sortable: true, filterable: true, width: 240 },
  { key: "referenceTypeLabel", header: "Reference Type", sortable: true, filterable: true, width: 220 },
  { key: "status", header: "Status", sortable: true, filterable: true, width: 130 },
  { key: "sortOrder", header: "Sort", sortable: true, filterable: false, width: 90, align: "right" },
  { key: "updatedAt", header: "Updated", sortable: true, filterable: false, width: 170 },
];

const commonFallbackFields: readonly EntitySchemaField[] = [
  { name: "code", label: "Code", type: "text", required: true, maxLength: 64 },
  { name: "label", label: "Label", type: "text", required: true, maxLength: 160 },
  { name: "description", label: "Description", type: "textarea", required: false, maxLength: 500 },
  { name: "sortOrder", label: "Sort order", type: "number", required: false },
  { name: "effectiveFrom", label: "Effective from", type: "date", required: false },
  { name: "effectiveTo", label: "Effective to", type: "date", required: false },
];

const categoryFallbackFields: readonly EntitySchemaField[] = [
  ...commonFallbackFields,
  { name: "parentCategoryId", label: "Parent category", type: "select", required: false, optionsSource: "complaint-category" },
];

const subcategoryFallbackFields: readonly EntitySchemaField[] = [
  { name: "parentCategoryId", label: "Complaint category", type: "select", required: true, optionsSource: "complaint-category" },
  ...commonFallbackFields,
];

const referenceDataFallbackFields: readonly EntitySchemaField[] = [
  { name: "referenceTypeId", label: "Reference type", type: "select", required: true, optionsSource: "reference-type" },
  ...commonFallbackFields,
];

const duplicateRule: CmtsMasterDataDomainRule = {
  code: "DUPLICATE_BUSINESS_KEY",
  message: "Business key is tenant + masterType + code. Duplicate codes are blocked before save and by backend conflict handling.",
};
const effectiveDateRule: CmtsMasterDataDomainRule = {
  code: "EFFECTIVE_DATE_ORDER",
  message: "Effective-to cannot be earlier than effective-from.",
};
const dropdownRefreshRule: CmtsMasterDataDomainRule = {
  code: "DROPDOWN_CONSUMER_REFRESH",
  message: "Successful mutations invalidate list/detail/dropdown consumers for complaint create, register, reports and dashboards.",
};

export const CMTS_MASTER_DATA_CRUD_REGISTRY = {
  severity: {
    entityKey: "severity",
    masterType: "severity",
    routeSegment: "severities",
    routePath: "/cmts/admin/master-data/severities",
    pageFile: "src/modules/pages/cmtsSeverityPage.tsx",
    serviceFile: "src/modules/services/severityApi.ts",
    serviceName: "severityApi",
    title: "Severities",
    singularTitle: "Severity",
    description: "Severity values used by complaint create/edit, risk classification, readiness, dashboards and reports.",
    permission: masterDataPermission,
    api: severityApi,
    columns: commonColumns,
    schemaMode: "backend",
    defaultSort: { sortBy: "sortOrder", sortDirection: "asc" },
    businessKey: ["tenantId", "masterType", "code"],
    dropdownConsumers: ["Complaint Create.severity", "Complaint Register filters.severity", "Report filters.severity", "Dashboard severity drilldowns"],
    dependencyTargets: ["complaints", "closure blockers", "dashboard KPIs", "report filters"],
    domainRules: [duplicateRule, effectiveDateRule, dropdownRefreshRule],
    fallbackFields: commonFallbackFields,
  },
  priority: {
    entityKey: "priority",
    masterType: "priority",
    routeSegment: "priorities",
    routePath: "/cmts/admin/master-data/priorities",
    pageFile: "src/modules/pages/cmtsPriorityPage.tsx",
    serviceFile: "src/modules/services/priorityApi.ts",
    serviceName: "priorityApi",
    title: "Priorities",
    singularTitle: "Priority",
    description: "Priority values used by complaint create/edit, SLA policy, escalation, dashboards and reports.",
    permission: masterDataPermission,
    api: priorityApi,
    columns: commonColumns,
    schemaMode: "backend",
    defaultSort: { sortBy: "sortOrder", sortDirection: "asc" },
    businessKey: ["tenantId", "masterType", "code"],
    dropdownConsumers: ["Complaint Create.priority", "Complaint Register filters.priority", "SLA policy.priority", "Escalation.priority", "Report filters.priority"],
    dependencyTargets: ["complaints", "SLA rules", "escalations", "dashboard KPIs", "report filters"],
    domainRules: [duplicateRule, effectiveDateRule, dropdownRefreshRule],
    fallbackFields: commonFallbackFields,
  },
  "complaint-category": {
    entityKey: "complaint-category",
    masterType: "complaint-category",
    routeSegment: "complaint-categories",
    routePath: "/cmts/admin/master-data/complaint-categories",
    pageFile: "src/modules/pages/cmtsComplaintCategoryPage.tsx",
    serviceFile: "src/modules/services/complaintCategoryApi.ts",
    serviceName: "complaintCategoryApi",
    title: "Complaint Categories",
    singularTitle: "Complaint Category",
    description: "Primary complaint classification values driving subcategory, assignment, SLA and dashboard classification.",
    permission: masterDataPermission,
    api: complaintCategoryApi,
    columns: categoryColumns,
    schemaMode: "backend",
    defaultSort: { sortBy: "sortOrder", sortDirection: "asc" },
    businessKey: ["tenantId", "masterType", "code"],
    dropdownConsumers: ["Complaint Create.category", "Complaint Register filters.category", "Report filters.category", "Subcategory parent dropdown"],
    dependencyTargets: ["complaints", "complaint subcategories", "routing rules", "SLA rules", "reports"],
    domainRules: [duplicateRule, effectiveDateRule, dropdownRefreshRule, { code: "ACTIVE_CHILD_DEPENDENCY_CHECK", message: "Category deactivation requires dependency check for active child categories/subcategories and complaint usage." }],
    fallbackFields: categoryFallbackFields,
  },
  "complaint-subcategory": {
    entityKey: "complaint-subcategory",
    masterType: "complaint-subcategory",
    routeSegment: "complaint-subcategories",
    routePath: "/cmts/admin/master-data/complaint-subcategories",
    pageFile: "src/modules/pages/cmtsComplaintSubcategoryPage.tsx",
    serviceFile: "src/modules/services/complaintSubcategoryApi.ts",
    serviceName: "complaintSubcategoryApi",
    title: "Complaint Subcategories",
    singularTitle: "Complaint Subcategory",
    description: "Subcategory values linked to an active complaint category and consumed by complaint routing, RCA/CAPA and reports.",
    permission: masterDataPermission,
    api: complaintSubcategoryApi,
    columns: subcategoryColumns,
    schemaMode: "backend",
    defaultSort: { sortBy: "sortOrder", sortDirection: "asc" },
    businessKey: ["tenantId", "masterType", "parentCategoryId", "code"],
    parent: {
      entityKey: "complaint-category",
      fieldName: "parentCategoryId",
      required: true,
      mustBeActive: true,
      dropdownLabel: "Complaint category",
      inactiveMessage: "Selected complaint category is inactive. Activate the category before assigning subcategories.",
    },
    dropdownConsumers: ["Complaint Create.subcategory", "Complaint Register filters.subcategory", "Report filters.subcategory", "RCA category analysis"],
    dependencyTargets: ["complaints", "routing rules", "RCA records", "reports"],
    domainRules: [duplicateRule, effectiveDateRule, dropdownRefreshRule, { code: "PARENT_REQUIRED", message: "Complaint Subcategory requires Complaint Category." }, { code: "PARENT_MUST_BE_ACTIVE", message: "Create/edit is blocked when the selected complaint category is inactive." }],
    fallbackFields: subcategoryFallbackFields,
  },
  "complaint-source": {
    entityKey: "complaint-source",
    masterType: "complaint-source",
    routeSegment: "complaint-sources",
    routePath: "/cmts/admin/master-data/complaint-sources",
    pageFile: "src/modules/pages/cmtsComplaintSourcePage.tsx",
    serviceFile: "src/modules/services/complaintSourceApi.ts",
    serviceName: "complaintSourceApi",
    title: "Complaint Sources",
    singularTitle: "Complaint Source",
    description: "Source/channel values used by complaint creation, register filters, dashboards and reports.",
    permission: masterDataPermission,
    api: complaintSourceApi,
    columns: commonColumns,
    schemaMode: "backend",
    defaultSort: { sortBy: "sortOrder", sortDirection: "asc" },
    businessKey: ["tenantId", "masterType", "code"],
    dropdownConsumers: ["Complaint Create.source", "Complaint Register filters.source", "Report filters.source", "Dashboard source drilldowns"],
    dependencyTargets: ["complaints", "routing rules", "report filters", "dashboard KPIs"],
    domainRules: [duplicateRule, effectiveDateRule, dropdownRefreshRule],
    fallbackFields: commonFallbackFields,
  },
  "reference-type": {
    entityKey: "reference-type",
    masterType: "reference-type",
    routeSegment: "reference-types",
    routePath: "/cmts/admin/master-data/reference-types",
    pageFile: "src/modules/pages/referenceTypePage.tsx",
    serviceFile: "src/modules/services/referenceTypeApi.ts",
    serviceName: "referenceTypeApi",
    title: "Reference Types",
    singularTitle: "Reference Type",
    description: "Reference type definitions used to group reference-data records and drive typed dropdowns.",
    permission: masterDataPermission,
    api: referenceTypeApi,
    columns: commonColumns,
    schemaMode: "backend",
    defaultSort: { sortBy: "sortOrder", sortDirection: "asc" },
    businessKey: ["tenantId", "masterType", "code"],
    dropdownConsumers: ["Reference Data.referenceType", "Generic reference filters", "Configuration forms"],
    dependencyTargets: ["reference-data records", "field schemas", "configuration rules"],
    domainRules: [duplicateRule, effectiveDateRule, dropdownRefreshRule, { code: "ACTIVE_CHILD_DEPENDENCY_CHECK", message: "Reference type deactivation requires dependency check for active reference-data children." }],
    fallbackFields: commonFallbackFields,
  },
  "reference-data": {
    entityKey: "reference-data",
    masterType: "reference-data",
    routeSegment: "reference-data",
    routePath: "/cmts/admin/master-data/reference-data",
    pageFile: "src/modules/pages/cmtsReferenceDataPage.tsx",
    serviceFile: "src/modules/services/referenceDataApi.ts",
    serviceName: "referenceDataApi",
    title: "Reference Data",
    singularTitle: "Reference Value",
    description: "Reference values linked to an active reference type for typed configuration and dropdown consumption.",
    permission: masterDataPermission,
    api: referenceDataApi,
    columns: referenceDataColumns,
    schemaMode: "backend",
    defaultSort: { sortBy: "sortOrder", sortDirection: "asc" },
    businessKey: ["tenantId", "masterType", "referenceTypeId", "code"],
    parent: {
      entityKey: "reference-type",
      fieldName: "referenceTypeId",
      required: true,
      mustBeActive: true,
      dropdownLabel: "Reference type",
      inactiveMessage: "Selected reference type is inactive. Activate the reference type before assigning reference values.",
    },
    dropdownConsumers: ["Typed reference dropdowns", "Configuration forms", "Report filters", "Audit filters"],
    dependencyTargets: ["configuration records", "field schemas", "workflow references", "reports"],
    domainRules: [duplicateRule, effectiveDateRule, dropdownRefreshRule, { code: "PARENT_REQUIRED", message: "Reference Data requires Reference Type." }, { code: "PARENT_MUST_BE_ACTIVE", message: "Create/edit is blocked when the selected reference type is inactive." }],
    fallbackFields: referenceDataFallbackFields,
  },
} as const satisfies Record<CmtsMasterDataCrudEntityKey, CmtsMasterDataCrudEntityConfig>;

export const CMTS_MASTER_DATA_CRUD_ENTITY_KEYS = Object.keys(CMTS_MASTER_DATA_CRUD_REGISTRY) as readonly CmtsMasterDataCrudEntityKey[];

export function cmtsMasterDataCrudConfigFor(entityKey: CmtsMasterDataCrudEntityKey): CmtsMasterDataCrudEntityConfig {
  return CMTS_MASTER_DATA_CRUD_REGISTRY[entityKey];
}

export function cmtsMasterDataCrudConfigForMasterType(masterType: string | undefined): CmtsMasterDataCrudEntityConfig | undefined {
  if (!masterType) return undefined;
  return CMTS_MASTER_DATA_CRUD_ENTITY_KEYS.map((key) => CMTS_MASTER_DATA_CRUD_REGISTRY[key]).find((config) => config.masterType === masterType);
}

export function cmtsMasterDataCrudConfigForRouteSegment(routeSegment: string | undefined): CmtsMasterDataCrudEntityConfig | undefined {
  if (!routeSegment) return undefined;
  return CMTS_MASTER_DATA_CRUD_ENTITY_KEYS.map((key) => CMTS_MASTER_DATA_CRUD_REGISTRY[key]).find((config) => config.routeSegment === routeSegment);
}
